import React, { useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import AuthModal from '../components/AuthModal';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { CheckCircle2, TrendingUp, Globe, Shield, Zap, Award, Target, BarChart3, Users, Clock, Star, ExternalLink, FileText, Eye, CreditCard, Layout, Link } from 'lucide-react';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_release-nexus/artifacts/a0bdi5ug_Alignk%20backlink%20professional.png';

const LandingPage = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState('login');

  const plans = [
    {
      id: 'basic',
      name: 'Starter',
      price: '$19.99',
      backlinks: 10,
      quality: 'Standard',
      features: [
        '10 Quality Backlinks',
        'DA 20-40 Websites',
        'Permanent Do-Follow Links',
        'Basic SEO Report'
      ]
    },
    {
      id: 'professional',
      name: 'Professional',
      price: '$49.99',
      backlinks: 23,
      quality: 'Premium',
      features: [
        '23 Quality Backlinks',
        'DA 40-60 Websites',
        'Permanent Do-Follow Links',
        'Detailed SEO Report',
        'Priority Support'
      ],
      popular: true
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: '$199.99',
      backlinks: 35,
      quality: 'Premium+',
      features: [
        '35 Quality Backlinks',
        'DA 50-80+ Websites',
        'Permanent Do-Follow Links',
        'Comprehensive SEO Report',
        'Dedicated Account Manager',
        'Custom Link Strategy'
      ]
    }
  ];

  const handleGetStarted = (planId) => {
    if (user) {
      navigate(`/checkout?plan=${planId}`);
    } else {
      setAuthMode('register');
      setShowAuth(true);
    }
  };

  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing');
    if (pricingSection) {
      pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const features = [
    {
      icon: <Link className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'High-Quality Backlinks',
      description: 'Get permanent do-follow backlinks from authority websites with high Domain Authority (DA) scores to boost your SEO.'
    },
    {
      icon: <Target className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Niche Relevant Links',
      description: 'We place your links on websites relevant to your industry for maximum SEO impact and referral traffic.'
    },
    {
      icon: <Zap className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Fast Delivery',
      description: 'Your backlinks are built and delivered within 5-7 business days with full transparency on placement.'
    },
    {
      icon: <Shield className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'White-Hat SEO Only',
      description: 'We use only Google-compliant link building strategies. No PBNs, no spam - just quality editorial links.'
    },
    {
      icon: <Globe className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Global Network',
      description: 'Access our network of 500+ authority websites across multiple industries and regions worldwide.'
    },
    {
      icon: <Award className="h-8 w-8" style={{ color: '#4AC5DB' }} />,
      title: 'Full Transparency',
      description: 'Receive detailed reports with live URLs, DA scores, and anchor text for every backlink placed.'
    }
  ];

  const benefits = [
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: 'Improve Search Rankings',
      description: 'Quality backlinks are the #1 ranking factor. Boost your position on Google search results.'
    },
    {
      icon: <Users className="h-6 w-6" />,
      title: 'Increase Domain Authority',
      description: 'Build your website\'s authority and trustworthiness with high-DA backlinks.'
    },
    {
      icon: <BarChart3 className="h-6 w-6" />,
      title: 'Drive Referral Traffic',
      description: 'Get targeted visitors from relevant websites linking to your content.'
    },
    {
      icon: <Clock className="h-6 w-6" />,
      title: 'Long-Term Results',
      description: 'Permanent backlinks that continue working for your SEO month after month.'
    }
  ];

  const process = [
    {
      step: '01',
      title: 'Choose Your Package',
      description: 'Select the backlink package that fits your SEO goals. Provide your website URL and target keywords.'
    },
    {
      step: '02',
      title: 'We Analyze Your Site',
      description: 'Our team reviews your website and identifies the best link opportunities in our network.'
    },
    {
      step: '03',
      title: 'Link Building Begins',
      description: 'We create high-quality content and secure editorial placements with backlinks to your site.'
    },
    {
      step: '04',
      title: 'Quality Assurance',
      description: 'Every backlink is verified for quality, relevance, and proper indexing before delivery.'
    },
    {
      step: '05',
      title: 'Detailed Report Delivered',
      description: 'Receive a comprehensive report with all live backlink URLs, DA scores, and placement details in your dashboard.'
    }
  ];

  const testimonials = [
    {
      name: 'S. Mitchell',
      role: 'E-commerce Owner',
      content: 'Alignk helped us climb from page 3 to page 1 for our main keywords. The backlinks are high quality and the reporting is excellent.'
    },
    {
      name: 'J. Anderson',
      role: 'Marketing Director',
      content: 'Finally, a backlink service that delivers what they promise. Our Domain Authority increased by 12 points in 3 months.'
    },
    {
      name: 'P. Sharma',
      role: 'SEO Agency Partner',
      content: 'We use Alignk for all our client link building. Consistent quality, transparent reporting, and great support.'
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 via-white to-teal-50">
      {/* Navigation */}
      <nav className="sticky top-0 z-50 backdrop-blur-lg bg-white/80 border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center cursor-pointer" onClick={() => navigate('/')}>
              <img src={LOGO_URL} alt="Alignk Logo" className="h-12 w-auto" />
            </div>
            <div className="flex items-center space-x-6">
              <Button
                variant="ghost"
                onClick={scrollToPricing}
                className="text-gray-700 hover:text-cyan-600 font-medium"
              >
                Pricing
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/services')}
                className="text-gray-700 hover:text-cyan-600 font-medium"
              >
                Services
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/about')}
                className="text-gray-700 hover:text-cyan-600 font-medium"
              >
                About Us
              </Button>
              <Button
                variant="ghost"
                onClick={() => navigate('/contact')}
                className="text-gray-700 hover:text-cyan-600 font-medium"
              >
                Contact
              </Button>
              {user ? (
                <>
                  <Button variant="ghost" onClick={() => navigate('/dashboard')}>
                    Dashboard
                  </Button>
                  <span className="text-sm text-gray-600">Hi, {user.name}</span>
                </>
              ) : (
                <>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setAuthMode('login');
                      setShowAuth(true);
                    }}
                  >
                    Login
                  </Button>
                  <Button
                    onClick={() => {
                      setAuthMode('register');
                      setShowAuth(true);
                    }}
                    style={{ backgroundColor: '#4AC5DB' }}
                    className="hover:opacity-90"
                  >
                    Get Started
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6" style={{ fontFamily: 'Playfair Display' }}>
              Buy <span style={{ color: '#4AC5DB' }}>Quality Backlinks</span> to Boost Your SEO
            </h1>
            <p className="text-lg md:text-xl text-gray-600 mb-8 leading-relaxed">
              Get high-authority, do-follow backlinks from real websites. Improve your search rankings, increase domain authority, and drive organic traffic with our white-hat link building service.
            </p>
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              <Button
                size="lg"
                onClick={scrollToPricing}
                className="text-lg px-8 py-6 hover:opacity-90"
                style={{ backgroundColor: '#4AC5DB' }}
              >
                View Backlink Packages
                <ExternalLink className="ml-2 h-5 w-5" />
              </Button>
            </div>
            <div className="flex flex-wrap justify-center gap-8 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>High DA Websites</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>Permanent Do-Follow Links</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-600" />
                <span>White-Hat SEO</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Backlink Sources - Scrolling Marquee */}
      <section className="py-10 bg-white border-y border-gray-100 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-center text-sm text-gray-500 mb-6 uppercase tracking-wider font-medium">
            Backlink Placement Network
          </p>
        </div>
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />
          
          <div className="flex overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap">
              <div className="flex items-center gap-12 px-6">
                <span className="text-xl font-black text-gray-500 tracking-tight">AP</span>
                <span className="text-xl font-bold text-gray-500 tracking-tight">Bloomberg</span>
                <span className="text-2xl font-bold text-gray-500" style={{ fontFamily: 'Georgia, serif' }}>Yahoo!</span>
                <span className="text-2xl font-black text-gray-500">abc</span>
                <span className="text-xl font-black text-gray-500">CBS</span>
                <span className="text-2xl font-black text-gray-500 tracking-tight">FOX</span>
                <span className="text-xl font-black text-gray-500">NBC</span>
                <span className="text-xl font-normal text-gray-500">Google</span>
                <span className="text-xl font-semibold text-gray-500 lowercase">bing</span>
                <span className="text-sm font-black text-gray-500 tracking-tight">USA TODAY</span>
                <span className="text-lg font-black text-gray-500">The CW</span>
                <span className="text-base font-bold text-gray-500">Moody&apos;s</span>
                <span className="text-base font-bold text-gray-500">MENAFN</span>
                <span className="text-sm font-bold text-gray-500">ITMunch</span>
                <span className="text-sm font-bold text-gray-500">MarTech</span>
              </div>
              <div className="flex items-center gap-12 px-6">
                <span className="text-xl font-black text-gray-500 tracking-tight">AP</span>
                <span className="text-xl font-bold text-gray-500 tracking-tight">Bloomberg</span>
                <span className="text-2xl font-bold text-gray-500" style={{ fontFamily: 'Georgia, serif' }}>Yahoo!</span>
                <span className="text-2xl font-black text-gray-500">abc</span>
                <span className="text-xl font-black text-gray-500">CBS</span>
                <span className="text-2xl font-black text-gray-500 tracking-tight">FOX</span>
                <span className="text-xl font-black text-gray-500">NBC</span>
                <span className="text-xl font-normal text-gray-500">Google</span>
                <span className="text-xl font-semibold text-gray-500 lowercase">bing</span>
                <span className="text-sm font-black text-gray-500 tracking-tight">USA TODAY</span>
                <span className="text-lg font-black text-gray-500">The CW</span>
                <span className="text-base font-bold text-gray-500">Moody&apos;s</span>
                <span className="text-base font-bold text-gray-500">MENAFN</span>
                <span className="text-sm font-bold text-gray-500">ITMunch</span>
                <span className="text-sm font-bold text-gray-500">MarTech</span>
              </div>
            </div>
          </div>
        </div>
        <p className="text-center text-xs text-gray-400 mt-6">
          *Backlinks are placed on high-authority websites. Actual placements vary by package and availability.
        </p>
        
        <style>{`
          @keyframes marquee {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-marquee {
            animation: marquee 25s linear infinite;
          }
          .animate-marquee:hover {
            animation-play-state: paused;
          }
        `}</style>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Why Buy Backlinks from <span style={{ color: '#4AC5DB' }}>Alignk</span>?
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We provide high-quality, SEO-friendly backlinks that actually work. No spam, no PBNs - just real editorial links from authority websites.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-2 hover:border-cyan-200 transition-all hover:shadow-lg">
                <CardHeader>
                  <div className="mb-4">{feature.icon}</div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-to-br from-cyan-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              What <span style={{ color: '#4AC5DB' }}>Backlinks</span> Can Do For Your Website
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Backlinks are votes of confidence from other websites. The more quality backlinks you have, the higher Google ranks your site.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-all">
                <div className="mb-4 p-3 rounded-full inline-block" style={{ backgroundColor: '#4AC5DB20' }}>
                  <div style={{ color: '#4AC5DB' }}>{benefit.icon}</div>
                </div>
                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                <p className="text-gray-600 text-sm">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Choose Your <span style={{ color: '#4AC5DB' }}>Backlink Package</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Affordable backlink packages for every budget. All links are permanent, do-follow, and from real authority websites.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative border-2 hover:shadow-xl transition-all ${
                  plan.popular ? 'border-cyan-500 shadow-lg scale-105' : 'border-gray-200'
                }`}
              >
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
                      Most Popular
                    </span>
                  </div>
                )}
                <CardHeader>
                  <CardTitle className="text-2xl" style={{ fontFamily: 'Playfair Display' }}>
                    {plan.name}
                  </CardTitle>
                  <CardDescription>
                    <div className="mt-4">
                      <span className="text-4xl font-bold" style={{ color: '#4AC5DB' }}>
                        {plan.price}
                      </span>
                    </div>
                    <p className="mt-2 text-sm">
                      {plan.backlinks} backlinks • {plan.quality} quality
                    </p>
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start">
                        <CheckCircle2 className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" style={{ color: '#4AC5DB' }} />
                        <span className="text-gray-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter>
                  <Button
                    className="w-full hover:opacity-90"
                    style={{ backgroundColor: plan.popular ? '#4AC5DB' : 'transparent' }}
                    variant={plan.popular ? 'default' : 'outline'}
                    onClick={() => handleGetStarted(plan.id)}
                  >
                    Buy Backlinks
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
          
          <p className="text-center text-sm text-gray-500 mt-8">
            All backlinks are permanent and do-follow. Placements subject to website availability.
          </p>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              How Our <span style={{ color: '#4AC5DB' }}>Link Building</span> Works
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              A simple, transparent process to get quality backlinks for your website.
            </p>
          </div>

          <div className="space-y-6">
            {process.map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-6 shadow-md hover:shadow-xl transition-all flex items-start gap-6">
                <div className="flex-shrink-0">
                  <div className="w-16 h-16 rounded-full flex items-center justify-center text-2xl font-bold text-white" style={{ backgroundColor: '#4AC5DB' }}>
                    {item.step}
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className="text-xl font-semibold mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Dashboard & Order Tracking Section */}
      <section className="py-20 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              Track Your Backlinks in Your <span style={{ color: '#4AC5DB' }}>Personal Dashboard</span>
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Complete transparency from purchase to delivery. Monitor your backlink order status and view all live links in real-time.
            </p>
          </div>

          {/* Visual Order Flow */}
          <div className="mb-16">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg mb-3">
                  <CreditCard className="h-10 w-10 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800">Place Order</h4>
                <p className="text-sm text-gray-500 max-w-[120px]">Secure checkout</p>
              </div>
              
              <div className="hidden md:block w-16 h-1 bg-gradient-to-r from-cyan-400 to-teal-400 mx-2" />
              <div className="md:hidden h-8 w-1 bg-gradient-to-b from-cyan-400 to-teal-400 my-2" />
              
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg mb-3">
                  <FileText className="h-10 w-10 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800">We Build Links</h4>
                <p className="text-sm text-gray-500 max-w-[120px]">Quality placements</p>
              </div>
              
              <div className="hidden md:block w-16 h-1 bg-gradient-to-r from-cyan-400 to-teal-400 mx-2" />
              <div className="md:hidden h-8 w-1 bg-gradient-to-b from-cyan-400 to-teal-400 my-2" />
              
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg mb-3">
                  <Globe className="h-10 w-10 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800">Links Go Live</h4>
                <p className="text-sm text-gray-500 max-w-[120px]">Indexed & active</p>
              </div>
              
              <div className="hidden md:block w-16 h-1 bg-gradient-to-r from-cyan-400 to-teal-400 mx-2" />
              <div className="md:hidden h-8 w-1 bg-gradient-to-b from-cyan-400 to-teal-400 my-2" />
              
              <div className="flex flex-col items-center text-center">
                <div className="w-20 h-20 rounded-full bg-gradient-to-br from-cyan-500 to-teal-500 flex items-center justify-center shadow-lg mb-3">
                  <Layout className="h-10 w-10 text-white" />
                </div>
                <h4 className="font-semibold text-gray-800">View Report</h4>
                <p className="text-sm text-gray-500 max-w-[120px]">Full link details</p>
              </div>
            </div>
          </div>

          {/* Dashboard Preview */}
          <div className="relative">
            <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-2xl p-6 md:p-8 shadow-2xl">
              <div className="flex items-center gap-2 mb-6">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <div className="flex-1 ml-4">
                  <div className="bg-gray-700 rounded-full px-4 py-1 text-sm text-gray-400 max-w-xs">
                    alignk.com/dashboard
                  </div>
                </div>
              </div>
              
              <div className="bg-white rounded-xl p-6 shadow-inner">
                <div className="flex items-center justify-between mb-6 pb-4 border-b">
                  <div>
                    <h3 className="text-xl font-bold text-gray-800">My Backlink Orders</h3>
                    <p className="text-sm text-gray-500">Track all your backlink purchases</p>
                  </div>
                  <div className="bg-cyan-100 text-cyan-700 px-3 py-1 rounded-full text-sm font-medium">
                    3 Active Orders
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
                          <CheckCircle2 className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">Professional Package</h4>
                          <p className="text-sm text-gray-500">Order #BL-2024-1847</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-block bg-green-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                          COMPLETED
                        </span>
                        <p className="text-sm text-gray-500 mt-1">23 Backlinks Live</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-3 border-t border-green-200 flex items-center justify-between">
                      <div className="flex items-center gap-4 text-sm text-gray-600">
                        <span className="flex items-center gap-1">
                          <Eye className="h-4 w-4" /> View Links
                        </span>
                        <span>Avg DA: 52</span>
                      </div>
                      <button className="text-cyan-600 text-sm font-medium hover:underline">
                        See All Backlinks →
                      </button>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-cyan-50 to-blue-50 rounded-lg p-4 border border-cyan-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-cyan-500 rounded-lg flex items-center justify-center">
                          <Clock className="h-6 w-6 text-white animate-pulse" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">Enterprise Package</h4>
                          <p className="text-sm text-gray-500">Order #BL-2024-1903</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-block bg-cyan-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                          IN PROGRESS
                        </span>
                        <p className="text-sm text-gray-500 mt-1">12/35 Links Built</p>
                      </div>
                    </div>
                    <div className="mt-3">
                      <div className="flex justify-between text-sm text-gray-600 mb-1">
                        <span>Link Building Progress</span>
                        <span>34%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-gradient-to-r from-cyan-500 to-teal-500 h-2 rounded-full" style={{ width: '34%' }} />
                      </div>
                    </div>
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg p-4 border border-amber-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-amber-500 rounded-lg flex items-center justify-center">
                          <FileText className="h-6 w-6 text-white" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">Starter Package</h4>
                          <p className="text-sm text-gray-500">Order #BL-2024-1956</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="inline-block bg-amber-500 text-white text-xs px-3 py-1 rounded-full font-medium">
                          PROCESSING
                        </span>
                        <p className="text-sm text-gray-500 mt-1">Site Analysis</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="absolute -bottom-6 left-1/2 transform -translate-x-1/2 flex gap-4">
              <div className="bg-white rounded-full px-4 py-2 shadow-lg border flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-gray-700">100% Transparent</span>
              </div>
              <div className="bg-white rounded-full px-4 py-2 shadow-lg border flex items-center gap-2 hidden sm:flex">
                <Eye className="h-5 w-5 text-cyan-500" />
                <span className="text-sm font-medium text-gray-700">Live Link Tracking</span>
              </div>
            </div>
          </div>

          {/* Feature Highlights */}
          <div className="mt-20 grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-cyan-100 flex items-center justify-center">
                <Eye className="h-8 w-8 text-cyan-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">See Every Backlink</h3>
              <p className="text-gray-600 text-sm">View all your live backlinks with direct URLs, DA scores, and anchor text.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-100 flex items-center justify-center">
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">SEO Reports</h3>
              <p className="text-gray-600 text-sm">Download comprehensive reports showing all backlink metrics and placement details.</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-purple-100 flex items-center justify-center">
                <Clock className="h-8 w-8 text-purple-600" />
              </div>
              <h3 className="text-lg font-semibold mb-2">Real-Time Status</h3>
              <p className="text-gray-600 text-sm">Track your order progress from purchase to completion with live updates.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-gradient-to-br from-gray-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display' }}>
              What Our <span style={{ color: '#4AC5DB' }}>Clients</span> Say
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Real results from businesses who bought backlinks from Alignk.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="border-2 border-gray-200 hover:border-cyan-200 transition-all">
                <CardHeader>
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <CardDescription className="text-gray-700 text-base italic">
                    &quot;{testimonial.content}&quot;
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="font-semibold">{testimonial.name}</p>
                  <p className="text-sm text-gray-500">{testimonial.role}</p>
                </CardContent>
              </Card>
            ))}
          </div>
          <p className="text-center text-xs text-gray-400 mt-8">
            *Testimonials represent individual customer experiences. Results may vary based on website, niche, and competition.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-cyan-500 to-teal-500">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" style={{ fontFamily: 'Playfair Display' }}>
            Ready to Boost Your SEO with Quality Backlinks?
          </h2>
          <p className="text-lg text-white/90 mb-8">
            Start building high-authority backlinks today. Choose your package and see results in your search rankings.
          </p>
          <Button
            size="lg"
            onClick={scrollToPricing}
            className="bg-white text-cyan-600 hover:bg-gray-100 text-lg px-8 py-6 font-semibold"
          >
            View Backlink Packages
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img 
                src={LOGO_URL} 
                alt="Alignk" 
                className="h-12 mb-4 brightness-0 invert" 
                style={{ filter: 'brightness(0) invert(1)' }}
              />
              <p className="text-gray-400 text-sm">
                Quality backlink building services for businesses. Boost your SEO with high-authority do-follow backlinks.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Quick Links</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li><a href="/about" className="hover:text-white transition">About Us</a></li>
                <li><a href="/services" className="hover:text-white transition">Services</a></li>
                <li><a href="/contact" className="hover:text-white transition">Contact</a></li>
                <li><a href="/faq" className="hover:text-white transition">FAQ</a></li>
                <li><a href="/privacy-policy" className="hover:text-white transition">Privacy Policy</a></li>
                <li><a href="/terms-of-use" className="hover:text-white transition">Terms of Use</a></li>
                <li><a href="/refund-policy" className="hover:text-white transition">Refund Policy</a></li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>Quality Backlinks</li>
                <li>High DA Link Building</li>
                <li>Niche Relevant Links</li>
                <li>SEO Services</li>
              </ul>
            </div>
            <div>
              <h3 className="font-semibold mb-4">Contact</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li>
                  <a href="tel:+442032397095" className="hover:text-white transition flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    +44 (0) 20323 97095
                  </a>
                </li>
                <li>
                  <a href="mailto:support@alignk.com" className="hover:text-white transition flex items-center gap-2">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    support@alignk.com
                  </a>
                </li>
                <li>
                  <a href="https://www.linkedin.com/company/alignkx/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                    LinkedIn
                  </a>
                </li>
                <li>
                  <a href="https://www.instagram.com/alignk.x/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition flex items-center gap-2">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                    Instagram
                  </a>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-center text-sm text-gray-400">
            <p>&copy; {new Date().getFullYear()} Alignk. All rights reserved.</p>
            <p className="mt-4 text-xs text-gray-500 max-w-3xl mx-auto">
              Disclaimer: Backlinks are one of many factors that influence search engine rankings. We do not guarantee specific ranking improvements, traffic increases, or business outcomes. Results depend on numerous factors including website quality, content, competition, and search engine algorithms. Individual results may vary.
            </p>
          </div>
        </div>
      </footer>

      {/* Auth Modal */}
      {showAuth && (
        <AuthModal
          isOpen={showAuth}
          onClose={() => setShowAuth(false)}
          mode={authMode}
        />
      )}
    </div>
  );
};

export default LandingPage;
